import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  CreateVendorRequestBody,
  LinkResponse,
  VendorRequestDetail,
  VendorRequestStatus,
  VendorRequestSummary,
} from './vendor-request-types'

const BASE = '/api/vendor-requests'
const KEY = ['vendor-requests'] as const

export function useVendorRequests(status: VendorRequestStatus | '') {
  return useQuery({
    queryKey: [...KEY, 'list', status],
    queryFn: () => jsonFetch<{ items: VendorRequestSummary[] }>(`${BASE}${status ? `?status=${status}` : ''}`),
    staleTime: 15_000,
  })
}

export function useVendorRequest(id: string | null) {
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => jsonFetch<VendorRequestDetail>(`${BASE}/${id}`),
    enabled: !!id,
    staleTime: 5_000,
  })
}

function useInvalidate() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: KEY })
}

export function useCreateVendorRequest() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (body: CreateVendorRequestBody) => jsonFetch<LinkResponse>(BASE, { method: 'POST', body }),
    onSuccess: invalidate,
  })
}

export function useNewVendorLink() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (vars: { id: string; days?: number }) =>
      jsonFetch<LinkResponse>(`${BASE}/${vars.id}/new-link`, { method: 'POST', body: { days: vars.days ?? null } }),
    onSuccess: invalidate,
  })
}

export function useReopenVendorRequest() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (vars: { id: string; note: string }) =>
      jsonFetch<LinkResponse>(`${BASE}/${vars.id}/reopen`, { method: 'POST', body: { note: vars.note } }),
    onSuccess: invalidate,
  })
}

export function useRevokeVendorRequest() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (id: string) => jsonFetch<VendorRequestSummary>(`${BASE}/${id}/revoke`, { method: 'POST' }),
    onSuccess: invalidate,
  })
}

export function useReviewVendorUpload() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (vars: { id: string; uploadId: number; status: 'accepted' | 'rejected'; note?: string }) =>
      jsonFetch<{ id: number; review_status: string }>(`${BASE}/${vars.id}/uploads/${vars.uploadId}/review`, {
        method: 'POST',
        body: { status: vars.status, note: vars.note || null },
      }),
    onSuccess: invalidate,
  })
}

export function useImportVendorRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      jsonFetch<{ intake_id: string; reference: string }>(`${BASE}/${id}/import`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY })
      void qc.invalidateQueries({ queryKey: ['product-onboarding'] })
    },
  })
}
